import { Badge } from '@/components/ui/badge.jsx';
import Panel from './Panel.jsx';

function Row({ label, value }) {
  if (value == null || value === '' || value === false) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-xs text-text-muted">{label}</span>
      <span className="text-right text-xs font-medium text-text">
        {value === true ? 'Yes' : value}
      </span>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h3 className="mt-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-dim first:mt-0">
      {children}
    </h3>
  );
}

export default function CommercialOperationsPanel({ manifest, loading }) {
  if (loading) {
    return <Panel title="Operations" empty="Loading…" />;
  }
  if (!manifest) {
    return <Panel title="Operations" empty="No manifest data — open Commercial Build to begin." />;
  }

  const { applicant, premises, fueling, hangarkeepers, personnel } = manifest;
  const tags = applicant?.business_type_tags || [];
  const hasAnyPremises = premises && (
    premises.offices || premises.tie_downs || premises.t_hangars ||
    premises.large_hangars || premises.maintenance_hangars || premises.paint_bays
  );

  return (
    <Panel title="Operations">
      {/* Business identity */}
      <SectionHeader>Business</SectionHeader>
      <div className="divide-y divide-border/50">
        <Row label="Named insured" value={applicant?.name} />
        {applicant?.dba ? <Row label="DBA" value={applicant.dba} /> : null}
        <Row label="Entity" value={applicant?.entity_type} />
        <Row label="In business since" value={applicant?.in_business_since} />
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 py-1.5">
            {tags.map((t) => (
              <Badge key={t} variant="outline" className="text-[9px]">
                {t.replace(/_/g, ' ').toUpperCase()}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>

      {/* Premises */}
      {hasAnyPremises ? (
        <>
          <SectionHeader>Premises</SectionHeader>
          <div className="divide-y divide-border/50">
            {premises.offices ? <Row label="Offices" value={premises.offices} /> : null}
            {premises.t_hangars ? <Row label="T-hangars" value={premises.t_hangars} /> : null}
            {premises.large_hangars ? <Row label="Large hangars" value={premises.large_hangars} /> : null}
            {premises.maintenance_hangars ? <Row label="Maintenance hangars" value={premises.maintenance_hangars} /> : null}
            {premises.tie_downs ? <Row label="Tie-downs" value={premises.tie_downs} /> : null}
            {premises.paint_bays ? <Row label="Paint bays" value={premises.paint_bays} /> : null}
            <Row label="Tower controlled" value={premises.tower_controlled} />
            <Row label="Airport fenced" value={premises.airport_fenced} />
          </div>
        </>
      ) : null}

      {/* Fueling */}
      {fueling && (fueling.fuel_trucks || fueling.fuel_farm || fueling.self_fueling) ? (
        <>
          <SectionHeader>Fueling</SectionHeader>
          <div className="divide-y divide-border/50">
            {fueling.fuel_trucks ? <Row label="Fuel trucks" value={fueling.fuel_trucks} /> : null}
            {fueling.fuel_farm ? <Row label="Fuel farm" value={fueling.fuel_farm} /> : null}
            {fueling.self_fueling ? <Row label="Self-fueling" value={fueling.self_fueling} /> : null}
            <Row label="Hot fueling" value={fueling.hot_fueling} />
          </div>
        </>
      ) : null}

      {/* Personnel */}
      {personnel?.total_w2_employees ? (
        <>
          <SectionHeader>Personnel</SectionHeader>
          <div className="divide-y divide-border/50">
            <Row label="W-2 employees" value={personnel.total_w2_employees} />
            <Row label="Uses subcontractors" value={personnel.uses_subcontractors} />
          </div>
        </>
      ) : null}

      {/* Hangarkeepers indicator */}
      {hangarkeepers && (hangarkeepers.tugs_aircraft_of_others || hangarkeepers.taxis_aircraft_of_others) ? (
        <>
          <SectionHeader>Hangarkeepers</SectionHeader>
          <div className="divide-y divide-border/50">
            <Row label="Tugs aircraft of others" value={hangarkeepers.tugs_aircraft_of_others} />
            <Row label="Taxis aircraft of others" value={hangarkeepers.taxis_aircraft_of_others} />
          </div>
        </>
      ) : null}
    </Panel>
  );
}
